/**
 * MergeHistoricalDialog (T3-075)
 * Admin-only, tenant-toggle gated. Merges a "loser" Historical (pre-go-live) contract into a
 * "survivor" Historical contract: the loser's rollover references + investors are repointed onto
 * the survivor and the loser is soft-deleted (hidden). Used to consolidate duplicate take-on stubs.
 *
 * Backed by POST /contracts/:survivorId/historical-merge -> sp_contract_merge_historical.
 * The loser is chosen via the historical-search autocomplete (GET /contracts/historical-search).
 */

import React, { useState, useEffect, useRef } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  DialogContentText,
  TextField,
  Typography,
} from '@mui/material';
import { apiPost, apiGet, extractApiData, extractApiError } from '../../utils/apiClient';
import logger from '../../utils/logger';

const fmtMaturity = (d) => (d
  ? new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
  : '');

const optionLabel = (o) => {
  if (!o) return '';
  const head = o.user_reference || o.reference || '';
  const sys = o.user_reference && o.reference ? ` (${o.reference})` : '';
  const mat = fmtMaturity(o.maturity_date);
  return `${head}${sys}${mat ? ` · mat ${mat}` : ''}`;
};

/**
 * @param {object} props
 * @param {boolean} props.open
 * @param {object} props.survivor - the Historical contract kept ({ id, reference, user_reference, ... })
 * @param {Function} props.onClose
 * @param {Function} props.onMerged - called after a successful merge (to refresh)
 */
const MergeHistoricalDialog = ({ open, survivor, onClose, onMerged }) => {
  const [loser, setLoser] = useState(null);
  const [searchOptions, setSearchOptions] = useState([]);
  const [searchInput, setSearchInput] = useState('');
  const [searchLoading, setSearchLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const searchDebounce = useRef(null);

  useEffect(() => {
    if (open) {
      setLoser(null);
      setSearchOptions([]);
      setSearchInput('');
      setError(null);
    }
  }, [open]);

  useEffect(() => {
    if (searchDebounce.current) clearTimeout(searchDebounce.current);
    const q = (searchInput || '').trim();
    if (q.length < 2) { setSearchOptions([]); setSearchLoading(false); return undefined; }
    setSearchLoading(true);
    searchDebounce.current = setTimeout(async () => {
      try {
        const res = await apiGet('/contracts/historical-search?q=' + encodeURIComponent(q));
        const rows = (extractApiData(res) || []).filter((o) => o.id !== survivor?.id);
        setSearchOptions(rows);
      } catch { setSearchOptions([]); } finally { setSearchLoading(false); }
    }, 350);
    return () => { if (searchDebounce.current) clearTimeout(searchDebounce.current); };
  }, [searchInput, survivor]);

  const canMerge = survivor && loser && loser.id !== survivor.id && !saving;

  const handleSubmit = async () => {
    if (!canMerge) return;
    setSaving(true);
    setError(null);
    try {
      await apiPost(`/contracts/${survivor.id}/historical-merge`, { loser_id: loser.id });
      if (onMerged) onMerged({ survivor, loser });
      onClose();
    } catch (err) {
      logger.error('Error merging historical contracts', { message: err.message, status: err.response?.status });
      setError(extractApiError(err, 'Failed to merge historical contracts'));
    } finally {
      setSaving(false);
    }
  };

  if (!survivor) return null;

  return (
    <Dialog open={open} onClose={() => { if (!saving) onClose(); }} maxWidth="sm" fullWidth>
      <DialogTitle>Merge Historical Contract</DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 2 }}>
          Fold another historical (pre-go-live) contract into this one. All rollover references and
          investors on the merged contract move to the kept contract, and the merged contract is
          hidden. Use this to consolidate duplicate take-on records. This cannot be undone from the UI.
        </DialogContentText>

        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

        <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Keep (survivor)</Typography>
        <TextField
          value={optionLabel(survivor)}
          fullWidth
          InputProps={{ readOnly: true }}
          variant="filled"
          sx={{ mb: 2 }}
        />

        <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Merge &amp; hide (loser)</Typography>
        <Autocomplete
          options={searchOptions}
          value={loser}
          onChange={(_, val) => setLoser(val)}
          onInputChange={(_, val, reason) => { if (reason === 'input') setSearchInput(val); }}
          loading={searchLoading}
          getOptionLabel={optionLabel}
          isOptionEqualToValue={(o, v) => o.id === v?.id}
          filterOptions={(x) => x}
          noOptionsText={searchInput.trim().length < 2 ? 'Type at least 2 characters' : 'No matching historical contracts'}
          renderOption={(props, option) => {
            const { key, ...rest } = props;
            return (
              <li key={option.id} {...rest}>
                <Box sx={{ width: '100%', py: 0.25 }}>
                  <Typography variant="body2" fontWeight="bold">{optionLabel(option)}</Typography>
                  <Typography variant="caption" color="textSecondary" component="div">
                    {[option.investor_names, option.product_type, `${option.currency_code || ''} ${option.capital_amount ?? ''}`.trim()]
                      .filter(Boolean).join(' · ')}
                  </Typography>
                </Box>
              </li>
            );
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Search a historical contract to merge in"
              helperText="Search by system reference, external reference, or investor"
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {searchLoading ? <CircularProgress color="inherit" size={16} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
        />

        {loser && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            <strong>{optionLabel(loser)}</strong> will be merged into <strong>{optionLabel(survivor)}</strong>
            {' '}and then hidden. Its rollover links and investors move to the kept contract.
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button onClick={handleSubmit} variant="contained" color="warning" disabled={!canMerge}>
          {saving ? 'Merging...' : 'Merge'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default MergeHistoricalDialog;
